import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import {
  addNewConfiguration,
  bulkImportConfigurations,
  isDirtyState,
  pasteNewConfiguration,
  resetSate,
  TenantConfiguration,
  TenantConfigurationSettings,
  tenantConfigurationState,
} from "../../redux/feature/settings/configurations-slice";
import { Configuration } from "./Configuration";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { CommandBarButton, DefaultButton } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { navigate } from "../../redux/feature/general/navigation-slice";
import { CHOOSECONFIG } from "../../utils/constants/navigation";
import FooterPanel from "../layout/FooterPanel";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import NotificationManager from "../../common/notification/NotificationManager";
import {
  IMPORT_DUPLICATE_CONFIGURATION_NOTIFICATION,
  PASTE_CONFIGURATION_NOTIFICATION,
} from "../../utils/constants/notification";
import { getClipboardText } from "../../utils/helpers/Clipboard";
import { saveAs } from "file-saver";
import { isOnWordWeb } from "../../api/config/ExternalConfig";

export interface ConfigurationsPaneProps {}

export const generateUniqueName = (baseName: string, takenNames: Set<string>): string => {
  if (!takenNames.has(baseName)) return baseName;
  let index = 1;
  while (takenNames.has(`${baseName}-${index}`)) {
    index++;
  }
  return `${baseName}-${index}`;
};

export const ConfigurationsPane: React.FunctionComponent<ConfigurationsPaneProps> = () => {
  const configurationState = useAppSelector(tenantConfigurationState);
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      dispatch(resetSate());
    };
  }, []);

  const configInUse = (conf: TenantConfiguration) => {
    let activeConf = configurationState.selected ? configurationState.selected : undefined;
    return activeConf === conf.name;
  };

  const configIsAuthenticated = (conf: TenantConfiguration) => {
    let activeConf = configurationState.selected ? configurationState.selected : undefined;
    return (
      activeConf === conf.name &&
      configurationState.configurations.find((value) => value.name === configurationState.selected)?.authData
    );
  };

  const createConf = () => {
    dispatch(addNewConfiguration());
  };

  const pasteConf = async () => {
    let text = await getClipboardText();
    let parsedConf: TenantConfigurationSettings;
    console.log("Pasted Configuration: " + text);
    try {
      parsedConf = JSON.parse(text);
      dispatch(pasteNewConfiguration(parsedConf));
    } catch (e) {
      console.error(e);
      NotificationManager.addNotification({
        key: PASTE_CONFIGURATION_NOTIFICATION,
        message: formatMessage("cof.access.manageconfigurations.message.pasteconfigurationerror"),
        title: "Error",
        type: "error",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
  };

  const exportConf = () => {
    const exportData = configurationState.configurations.map((conf) => ({
      name: conf.name,
      tenantId: conf.tenantId,
      cpmUrl: conf.url ?? "",
      cpmCustomerName: "",
      idpAppRegistrationId: conf.idpClientId,
      sharePointTenantId: "",
      sharePointHostName: "",
      sharePointSite: "",
      environmentInfo: {
        idpUrl: conf.idpUrl ?? "",
        tagetikUrl: conf.url ?? "",
      },
    }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    saveAs(blob, "configurations.json");
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result as string);
        if (!Array.isArray(data)) throw new Error("Expected an array");

        const existingTenantIds = new Set(configurationState.configurations.map((c) => c.tenantId).filter(Boolean));
        const takenNames = new Set(configurationState.configurations.map((c) => c.name).filter(Boolean));

        const skipped: Array<{ name: string; tenantId: string }> = [];
        const toImport: Array<TenantConfigurationSettings & { name: string }> = [];
        const seenBatchTenantIds = new Set<string>();
        const duplicatesInBatch: Array<{ name: string; tenantId: string }> = [];

        for (const item of data as Array<TenantConfigurationSettings & { name: string }>) {
          if (!item.name || !item.tenantId || !item.idpAppRegistrationId) {
            skipped.push({ name: item.name ?? "", tenantId: item.tenantId ?? "" });
            continue;
          }
          if (existingTenantIds.has(item.tenantId)) {
            skipped.push({ name: item.name, tenantId: item.tenantId });
            continue;
          }
          if (seenBatchTenantIds.has(item.tenantId)) {
            duplicatesInBatch.push({ name: item.name, tenantId: item.tenantId });
            continue;
          }
          const uniqueName = generateUniqueName(item.name, takenNames);
          takenNames.add(uniqueName);
          toImport.push({ ...item, name: uniqueName });
          seenBatchTenantIds.add(item.tenantId);
        }

        if (toImport.length > 0) {
          dispatch(bulkImportConfigurations(toImport));
        }

        if (skipped.length > 0) {
          const list = skipped.map((s) => `${s.name} (${s.tenantId})`).join(", ");
          NotificationManager.addNotification({
            key: PASTE_CONFIGURATION_NOTIFICATION,
            message: formatMessageWithParams("cof.access.manageconfigurations.import.errormessage", { list }),
            title: "",
            type: "warning",
            closable: true,
            removeOnClose: true,
            isTemporary: true,
          });
        }

        if (duplicatesInBatch.length > 0) {
          const list = duplicatesInBatch.map((s) => `${s.name} (${s.tenantId})`).join(", ");
          NotificationManager.addNotification({
            key: IMPORT_DUPLICATE_CONFIGURATION_NOTIFICATION,
            message: formatMessageWithParams("cof.access.manageconfigurations.import.duplicatetenantwarning", { list }),
            title: "",
            type: "warning",
            closable: true,
            removeOnClose: true,
            isTemporary: true,
          });
        }
      } catch (err) {
        console.error(err);
        NotificationManager.addNotification({
          key: PASTE_CONFIGURATION_NOTIFICATION,
          message: formatMessage("cof.access.manageconfigurations.message.pasteconfigurationerror"),
          title: "Error",
          type: "error",
          closable: true,
          removeOnClose: true,
          isTemporary: true,
        });
      } finally {
        e.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  return (
    <div style={{ padding: "0.5rem" }}>
      <div
        style={{
          fontSize: "1rem",
          fontWeight: "500",
          lineHeight: "1.25rem",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: "var(--color-blue)",
        }}
      >
        {formatMessage("cof.access.manageconfigurations")}
      </div>
      <div
        style={{
          fontWeight: "500",
          padding: "0.5rem 0 0",
          fontSize: "0.875rem",
          color: "var(--color-grey)",
        }}
      >
        {formatMessage("cof.access.manageconfigurations.info")}
      </div>
      {configurationState.configurations.map((conf, index) => (
        <div className={"shadowedCard"} key={index}>
          <Configuration
            index={index}
            key={index}
            inUse={configInUse(conf)}
            authenticated={configIsAuthenticated(conf)}
          />
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "end" }}>
        <CommandBarButton
          iconProps={{ iconName: "CircleAdditionSolid" }}
          onClick={createConf}
          style={{
            textOverflow: "ellipsis",
            marginBottom: "5px",
          }}
          disabled={isDirtyState(configurationState)}
          text={formatMessage("cof.access.manageconfigurations.addconfiguration")}
        />
        {!isOnWordWeb() && (
          <CommandBarButton
            iconProps={{ iconName: "Paste" }}
            onClick={pasteConf}
            style={{
              textOverflow: "ellipsis",
              marginBottom: "5px",
            }}
            disabled={isDirtyState(configurationState)}
            text={formatMessage("cof.access.manageconfigurations.pasteconfiguration")}
          />
        )}
      </div>
      <FooterPanel>
        <PrimaryButton
          className={ButtonVariants.white}
          text={formatMessage("cof.base.back")}
          style={{ float: "left" }}
          onClick={() => dispatch(navigate(CHOOSECONFIG))}
        />
        <DefaultButton
          className={ButtonVariants.primary}
          style={{ float: "right", width: 160, minWidth: 160, overflow: "hidden" }}
          text={formatMessage("cof.access.manageconfigurations.importexport")}
          disabled={isDirtyState(configurationState)}
          menuProps={{
            items: [
              {
                key: "import",
                text: formatMessage("cof.access.manageconfigurations.import"),
                onClick: () => fileInputRef.current?.click(),
              },
              {
                key: "export",
                text: formatMessage("cof.access.manageconfigurations.export"),
                onClick: exportConf,
              },
            ],
            isBeakVisible: false,
          }}
        />
        <input type="file" accept=".json" ref={fileInputRef} style={{ display: "none" }} onChange={handleImportFile} />
      </FooterPanel>
    </div>
  );
};
